/**
 * BinManager Component
 * Full-featured media library with drag-drop upload, grid view, and timeline integration
 * 
 * Features:
 * - Rushes: Imported media files
 * - Generated: AI-generated videos
 * - Sequences: Découpage bins with slots (NEW)
 */

import React, { useState, useRef, useCallback } from 'react';
import { useLocalMediaLibrary } from '../hooks/useLocalMediaLibrary';
import { useSequenceBins } from '../hooks/useSequenceBins';
import { useCloudAssets, CloudAsset } from '../hooks/useCloudAssets';
import { RushMedia } from '../types/media';
import { SavedShot } from '../types';
import { SequenceBinFolder } from './SequenceBinFolder';
import { downloadMedia } from '../utils/fileDownload';

interface BinManagerProps {
    onMediaSelect?: (media: RushMedia) => void;
    onAddToTimeline?: (media: RushMedia) => void;
    onSlotGenerate?: (binId: string, slotId: string) => void;
    onSlotAddToTimeline?: (binId: string, slotId: string) => void;
    projectShots?: SavedShot[];
}

export const BinManager: React.FC<BinManagerProps> = ({
    onMediaSelect,
    onAddToTimeline,
    onSlotGenerate,
    onSlotAddToTimeline,
    projectShots = [],
}) => {
    const { rushes, generated, isLoading, uploadMedia, deleteMedia, renameMedia } = useLocalMediaLibrary();
    const {
        bins,
        toggleBinExpanded,
        renameBin,
        deleteBin,
        addAllPendingToQueue,
        getSlotById,
    } = useSequenceBins();
    const { assets: cloudAssets, fetchHD, getViewLink, removeAsset: removeCloudAsset } = useCloudAssets();

    const [activeTab, setActiveTab] = useState<'rushes' | 'generated' | 'sequences' | 'cloud'>('rushes');
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [filter, setFilter] = useState('');
    const [contextMenu, setContextMenu] = useState<{ x: number; y: number; media: RushMedia } | null>(null);
    const [renamingId, setRenamingId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [selectedBinId, setSelectedBinId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string>>(new Set());

    // Merge Local Generated + Project Library Shots
    const allGeneratedMedia = React.useMemo(() => {
        const libraryMedia: RushMedia[] = projectShots.map(shot => ({
            id: shot.id,
            name: shot.title || shot.prompt?.substring(0, 30) || 'Untitled Shot',
            type: 'video',
            mimeType: 'video/mp4',
            localUrl: shot.videoUrl || '',
            thumbnail: shot.thumbnail || '', // base64
            durationSec: 8, // Default for Veo shots
            sizeBytes: 0,
            createdAt: new Date(shot.createdAt).getTime(),
        }));

        // Deduplicate by ID
        const combined = [...generated];
        libraryMedia.forEach(libItem => {
            if (!combined.find(c => c.id === libItem.id)) {
                combined.push(libItem);
            }
        });

        return combined.sort((a, b) => b.createdAt - a.createdAt);
    }, [generated, projectShots]);

    const currentMedia = React.useMemo(() => {
        if (activeTab === 'rushes') return rushes;
        return allGeneratedMedia;
    }, [activeTab, rushes, allGeneratedMedia]);

    const filteredMedia = filter
        ? currentMedia.filter(m => m.name.toLowerCase().includes(filter.toLowerCase()))
        : currentMedia;

    const filteredBins = filter
        ? bins.filter(b => b.name.toLowerCase().includes(filter.toLowerCase()))
        : bins;

    // Handle Selection Logic
    const handleMediaClick = (e: React.MouseEvent, mediaId: string) => {
        // e.stopPropagation(); // Allow bubbling to close context menu if needed? No, handled by click outside.

        if (e.ctrlKey || e.metaKey) {
            // Toggle
            const newSet = new Set(selectedMediaIds);
            if (newSet.has(mediaId)) newSet.delete(mediaId);
            else newSet.add(mediaId);
            setSelectedMediaIds(newSet);
        } else if (e.shiftKey) {
            // Range Select (Simplistic: from last selected to current)
            // For now, simpler shift behavior: just add to selection? 
            // Proper Range Select requires tracking "lastFocused". Let's stick to Toggle or Single for now.
            const newSet = new Set(selectedMediaIds);
            newSet.add(mediaId);
            setSelectedMediaIds(newSet);
        } else {
            // Single Select
            setSelectedMediaIds(new Set([mediaId]));
        }
    };

    // Bulk Actions
    const handleBulkDelete = () => {
        if (selectedMediaIds.size === 0) return;
        if (confirm(`Supprimer ${selectedMediaIds.size} éléments ?`)) {
            selectedMediaIds.forEach(id => {
                deleteMedia(id, activeTab as 'rushes' | 'generated');
            });
            setSelectedMediaIds(new Set());
        }
    };

    const handleBulkDownload = () => {
        if (selectedMediaIds.size === 0) return;
        // Download sequentially or zip? Sequentially for now (browser may block > 10 popups)
        const selectedItems = currentMedia.filter(m => selectedMediaIds.has(m.id));
        selectedItems.forEach(media => {
            downloadMedia(media.localUrl, media.name, media.mimeType);
        });
    };

    const handleBulkAddToTimeline = () => {
        if (selectedMediaIds.size === 0 || !onAddToTimeline) return;
        const selectedItems = currentMedia.filter(m => selectedMediaIds.has(m.id));
        // Add in order of appearance
        selectedItems.forEach(media => {
            onAddToTimeline(media);
        });
        // Clear selection? Maybe keep it for repeated use.
    };

    // Handle file drop
    const handleDrop = useCallback(async (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        if (activeTab === 'sequences') return; // No upload in sequences tab

        const files = Array.from(e.dataTransfer.files).filter(
            f => f.type.startsWith('video/') || f.type.startsWith('image/')
        );

        if (files.length === 0) return;

        setIsUploading(true);
        setUploadProgress(0);

        for (let i = 0; i < files.length; i++) {
            await uploadMedia(files[i], activeTab as 'rushes' | 'generated');
            setUploadProgress(((i + 1) / files.length) * 100);
        }

        setIsUploading(false);
        setUploadProgress(0);
    }, [uploadMedia, activeTab]);

    // Handle file input
    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setIsUploading(true);
        setUploadProgress(0);

        for (let i = 0; i < files.length; i++) {
            await uploadMedia(files[i], activeTab as 'rushes' | 'generated');
            setUploadProgress(((i + 1) / files.length) * 100);
        }

        setIsUploading(false);
        setUploadProgress(0);

        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [uploadMedia, activeTab]);

    // Context menu handlers
    const handleContextMenu = (e: React.MouseEvent, media: RushMedia) => {
        e.preventDefault();
        // If right-clicked item is not in selection, select it solely
        if (!selectedMediaIds.has(media.id)) {
            setSelectedMediaIds(new Set([media.id]));
        }
        setContextMenu({ x: e.clientX, y: e.clientY, media });
    };

    const closeContextMenu = () => setContextMenu(null);

    const handleRename = (media: RushMedia) => {
        setRenamingId(media.id);
        setRenameValue(media.name);
        closeContextMenu();
    };

    const submitRename = (media: RushMedia) => {
        if (renameValue.trim()) {
            renameMedia(media.id, renameValue.trim(), activeTab as 'rushes' | 'generated');
        }
        setRenamingId(null);
        setRenameValue('');
    };

    const handleDelete = (media: RushMedia) => {
        if (confirm(`Supprimer "${media.name}" ?`)) {
            deleteMedia(media.id, activeTab as 'rushes' | 'generated');
        }
        closeContextMenu();
    };

    // Clear selection when clicking empty area
    const handleBackgroundClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            setSelectedMediaIds(new Set());
            closeContextMenu();
        } else {
            closeContextMenu();
        }
    };

    // Slot handlers
    const handleSlotClick = (binId: string, slotId: string) => {
        const slot = getSlotById(binId, slotId);
        if (slot?.video) {
            // Convert to RushMedia format for preview
            const media: RushMedia = {
                id: slotId,
                name: `${slot.shotType} (${slot.duration}s)`,
                type: 'video',
                mimeType: 'video/mp4',
                localUrl: slot.video.url,
                thumbnail: slot.video.thumbnail,
                durationSec: Number(slot.duration),
                sizeBytes: 0,
                createdAt: new Date(slot.createdAt).getTime(),
            };
            onMediaSelect?.(media);
        }
    };

    const handleSlotGenerate = (binId: string, slotId: string) => {
        onSlotGenerate?.(binId, slotId);
    };

    const handleSlotAddToTimeline = (binId: string, slotId: string) => {
        onSlotAddToTimeline?.(binId, slotId);
    };

    // Format helpers
    const formatDuration = (sec?: number) => {
        if (!sec) return '';
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const formatSize = (bytes: number) => {
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    // Calculate totals for badges
    const totalBinsSlots = bins.reduce((sum, b) => sum + b.slots.length, 0);
    const readyBinsSlots = bins.reduce((sum, b) => sum + b.readyCount, 0);

    return (
        <aside className="w-72 flex flex-col bg-[#161616] border-r border-[#3f3f46] shrink-0 z-20 h-full relative">
            {/* Header */}
            <div className="h-14 border-b border-[#3f3f46] flex items-center px-4 justify-between bg-[#1e1e1e] shrink-0">
                <h2 className="font-semibold text-sm text-gray-200">Bin Manager</h2>
                {activeTab !== 'sequences' && (
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="text-gray-400 hover:text-white transition-colors"
                        title="Importer des fichiers"
                    >
                        <span className="material-symbols-outlined text-lg">cloud_upload</span>
                    </button>
                )}
            </div>

            {/* Tabs & Filter */}
            <div className="p-3 border-b border-[#3f3f46] bg-[#1a1a1a] shrink-0">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex bg-black/40 rounded p-0.5 border border-[#3f3f46]">
                        <button
                            onClick={() => setActiveTab('rushes')}
                            className={`px-2 py-1 text-[10px] font-medium rounded-sm transition-colors ${activeTab === 'rushes'
                                ? 'bg-gray-700 text-white shadow-sm'
                                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                                }`}
                        >
                            Rushes ({rushes.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('generated')}
                            className={`px-2 py-1 text-[10px] font-medium rounded-sm transition-colors ${activeTab === 'generated'
                                ? 'bg-gray-700 text-white shadow-sm'
                                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                                }`}
                        >
                            Générés ({allGeneratedMedia.length})
                        </button>
                        <button
                            onClick={() => setActiveTab('sequences')}
                            className={`px-2 py-1 text-[10px] font-medium rounded-sm transition-colors ${activeTab === 'sequences'
                                ? 'bg-indigo-700 text-white shadow-sm'
                                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                                }`}
                        >
                            <span className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-xs">folder_special</span>
                                Séq ({bins.length})
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('cloud')}
                            className={`px-2 py-1 text-[10px] font-medium rounded-sm transition-colors ${activeTab === 'cloud'
                                ? 'bg-emerald-700 text-white shadow-sm'
                                : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800/50'
                                }`}
                        >
                            <span className="flex items-center gap-1">
                                <span className="material-symbols-outlined text-xs">cloud</span>
                                Drive ({cloudAssets.length})
                            </span>
                        </button>
                    </div>
                </div>
                <div className="relative">
                    <span className="absolute left-2 top-1.5 text-gray-500 material-symbols-outlined text-sm">search</span>
                    <input
                        className="w-full pl-8 pr-2 py-1.5 text-xs bg-black/40 border border-gray-700 rounded text-gray-200 placeholder-gray-600 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                        placeholder={activeTab === 'sequences' ? "Filtrer séquences..." : "Filtrer média..."}
                        type="text"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
            </div>

            {/* Content Area */}
            <div
                className={`flex-1 overflow-y-auto p-3 ${activeTab === 'sequences' ? 'space-y-2' : 'grid grid-cols-2 gap-3 content-start'} bg-[#121212] ${isDragging && activeTab !== 'sequences' ? 'ring-2 ring-indigo-500 ring-inset' : ''
                    }`}
                onDragOver={(e) => { e.preventDefault(); if (activeTab !== 'sequences') setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleDrop}
                onClick={handleBackgroundClick}
            >
                {/* === SEQUENCES TAB === */}
                {activeTab === 'sequences' && (
                    <>
                        {/* Sequence Bins List */}
                        {filteredBins.map(bin => (
                            <SequenceBinFolder
                                key={bin.id}
                                bin={bin}
                                isSelected={selectedBinId === bin.id}
                                onSelect={setSelectedBinId}
                                onToggleExpand={toggleBinExpanded}
                                onSlotClick={handleSlotClick}
                                onSlotGenerate={handleSlotGenerate}
                                onSlotAddToTimeline={handleSlotAddToTimeline}
                                onGenerateAll={addAllPendingToQueue}
                                onRename={renameBin}
                                onDelete={deleteBin}
                            />
                        ))}
                    </>
                )}

                {/* === CLOUD TAB (Simplification: Use same grid for Cloud assets?) === */}
                {/* Keep existing logic for Cloud tab but apply similar visual updates if needed. */}
                {/* For brevity, keeping cloud rendering roughly same but checking if we need selection there too? */}
                {/* User seems focused on Rushes/Generated management mainly. */}
                {activeTab === 'cloud' && (
                    <>
                        {cloudAssets.map(asset => (
                            <div key={asset.id} className="group relative aspect-video bg-gray-800 rounded border border-transparent hover:border-emerald-500">
                                {/* Simple Cloud View (Read Only mostly) */}
                                {asset.thumbnailBase64 ? (
                                    <img src={`data:image/jpeg;base64,${asset.thumbnailBase64}`} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-emerald-600"><span className="material-symbols-outlined">cloud</span></div>
                                )}
                                <div className="absolute bottom-0 w-full bg-black/60 p-1">
                                    <div className="text-[9px] text-white truncate">{asset.filename}</div>
                                </div>
                            </div>
                        ))}
                    </>
                )}


                {/* === RUSHES / GENERATED TABS === */}
                {(activeTab === 'rushes' || activeTab === 'generated') && (
                    <>
                        {/* Upload Zone */}
                        <div
                            className={`col-span-2 border-2 border-dashed rounded-lg h-24 flex flex-col items-center justify-center transition-colors cursor-pointer mb-2 ${isDragging
                                ? 'border-indigo-500 bg-indigo-900/20 text-indigo-300'
                                : 'border-gray-800 text-gray-600 hover:border-gray-500 bg-gray-900/20'
                                }`}
                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                        >
                            {isUploading ? (
                                <div className="text-center">
                                    <div className="w-24 h-2 bg-gray-800 rounded-full overflow-hidden mb-2 mx-auto">
                                        <div className="h-full bg-indigo-500" style={{ width: `${uploadProgress}%` }} />
                                    </div>
                                    <span className="text-[10px]">Uploading...</span>
                                </div>
                            ) : (
                                <span className="material-symbols-outlined text-2xl text-gray-600">add</span>
                            )}
                        </div>

                        {/* Media Items */}
                        {filteredMedia.map(media => {
                            const isSelected = selectedMediaIds.has(media.id);
                            return (
                                <div
                                    key={media.id}
                                    className={`group relative aspect-video bg-gray-800 rounded overflow-hidden border cursor-pointer select-none transition-all
                                    ${isSelected
                                            ? 'border-indigo-500 ring-2 ring-indigo-500/50 z-10'
                                            : 'border-transparent hover:border-gray-500'
                                        }
                                `}
                                    onClick={(e) => handleMediaClick(e, media.id)}
                                    onContextMenu={(e) => handleContextMenu(e, media)}
                                    onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        onMediaSelect?.(media);
                                    }}
                                    draggable
                                    onDragStart={(e) => {
                                        e.dataTransfer.setData('application/json', JSON.stringify(media));
                                        e.dataTransfer.effectAllowed = 'copy';
                                    }}
                                >
                                    {/* Thumbnail */}
                                    {media.thumbnail ? (
                                        <img src={media.thumbnail} alt={media.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-900/50">
                                            <span className="material-symbols-outlined text-gray-700 text-3xl">
                                                {media.type === 'video' ? 'movie' : 'image'}
                                            </span>
                                        </div>
                                    )}

                                    {/* Duration Badge */}
                                    {media.type === 'video' && media.durationSec && (
                                        <div className="absolute top-1 right-1 text-[9px] bg-black/80 text-white px-1 py-0.5 rounded font-mono">
                                            {formatDuration(media.durationSec)}
                                        </div>
                                    )}

                                    {/* Selection Indicator (Checkmark) */}
                                    {isSelected && (
                                        <div className="absolute top-1 left-1 bg-indigo-600 rounded-full p-0.5 shadow-sm">
                                            <span className="material-symbols-outlined text-white text-[10px] block">check</span>
                                        </div>
                                    )}

                                    {/* Name Overlay */}
                                    <div className={`absolute bottom-0 left-0 right-0 p-1.5 pt-4 transition-all ${isSelected ? 'bg-indigo-900/80' : 'bg-gradient-to-t from-black/90 to-transparent'}`}>
                                        {renamingId === media.id ? (
                                            <input
                                                type="text"
                                                value={renameValue}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onBlur={() => submitRename(media)}
                                                onKeyDown={(e) => e.key === 'Enter' && submitRename(media)}
                                                className="w-full text-[10px] bg-black/50 border border-indigo-500 rounded px-1 py-0.5 text-white focus:outline-none"
                                                autoFocus
                                                onClick={e => e.stopPropagation()}
                                            />
                                        ) : (
                                            <p className={`text-[10px] truncate font-medium ${isSelected ? 'text-white' : 'text-gray-200'}`}>{media.name}</p>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </>
                )}
            </div>

            {/* === FOOTER ACTION BAR === */}
            {selectedMediaIds.size > 0 && (
                <div className="absolute bottom-4 left-4 right-4 bg-[#1e1e1e] border border-gray-700 rounded-lg shadow-2xl p-2 flex items-center justify-between z-50 animate-in slide-in-from-bottom-4 duration-200">
                    <span className="text-xs font-medium text-white ml-2">
                        {selectedMediaIds.size} sélectionné{selectedMediaIds.size > 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-1">
                        {onAddToTimeline && (
                            <button
                                onClick={handleBulkAddToTimeline}
                                className="p-1.5 hover:bg-indigo-600/30 text-indigo-400 rounded transition-colors"
                                title="Ajouter à la timeline"
                            >
                                <span className="material-symbols-outlined text-lg">add_to_queue</span>
                            </button>
                        )}
                        <button
                            onClick={handleBulkDownload}
                            className="p-1.5 hover:bg-gray-700 text-gray-300 rounded transition-colors"
                            title="Télécharger"
                        >
                            <span className="material-symbols-outlined text-lg">download</span>
                        </button>
                        <div className="w-px h-4 bg-gray-700 mx-1"></div>
                        <button
                            onClick={handleBulkDelete}
                            className="p-1.5 hover:bg-red-900/30 text-red-400 rounded transition-colors"
                            title="Supprimer"
                        >
                            <span className="material-symbols-outlined text-lg">delete</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Hidden File Input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="video/*,image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
            />

            {/* Context Menu (Simplified) */}
            {contextMenu && (
                <div
                    className="fixed z-50 bg-gray-900 border border-gray-700 rounded-lg shadow-xl py-1 min-w-[160px]"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                    onClick={(e) => { e.stopPropagation(); closeContextMenu(); }}
                >
                    <button
                        onClick={() => handleRename(contextMenu.media)}
                        className="w-full px-3 py-2 text-sm text-left text-gray-200 hover:bg-gray-700 flex items-center gap-2"
                    >
                        <span className="material-symbols-outlined text-base">edit</span>
                        Renommer
                    </button>
                    {/* Only show 'Delete' in context menu if single item (otherwise confusing?) 
                        Actually nice to have quick actions. 
                        Let's keep them but make sure they handle selection properly? 
                        For now, context menu acts on the SPECIFIC item right clicked.
                    */}
                    <div className="border-t border-gray-700 my-1" />
                    <button
                        onClick={() => handleDelete(contextMenu.media)}
                        className="w-full px-3 py-2 text-sm text-left text-red-400 hover:bg-red-900/50 flex items-center gap-2"
                    >
                        <span className="material-symbols-outlined text-base">delete</span>
                        Supprimer
                    </button>
                </div>
            )}
        </aside>
    );
};

export default BinManager;
